import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';

const TeamMemberBillingConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  billingImpact,
  loading = false,
  confirmLabelKey = 'teamMembers.billing.confirmAdd',
}) => {
  const { t } = useTranslation();

  if (!billingImpact?.willIncurAdditionalCharge) {
    return null;
  }

  const currency = (billingImpact.currency || 'EUR').toUpperCase();
  const seatPriceLabel = formatCurrency(billingImpact.additionalSeatUnitAmount, currency);
  const recurringLabel = formatCurrency(billingImpact.recurringMonthlyIncrease, currency);
  const immediateLabel = billingImpact.estimatedImmediateCharge != null
    ? formatCurrency(billingImpact.estimatedImmediateCharge, currency)
    : null;
  const periodEndLabel = billingImpact.currentPeriodEndUtc
    ? formatUtcDate(billingImpact.currentPeriodEndUtc)
    : null;

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('teamMembers.billing.confirmTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{
        flexShrink: 0,
        justifyContent: 'space-between',
      }}
      actions={(
        <>
          <Button onClick={onClose} color="inherit" disabled={loading}>
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={onConfirm} disabled={loading}>
            {t(confirmLabelKey)}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body1">
          {t('teamMembers.billing.confirmIntro', { price: seatPriceLabel })}
        </Typography>

        {billingImpact.isTrial ? (
          <Alert severity="info">
            {t('teamMembers.billing.trialNotice', { price: recurringLabel })}
          </Alert>
        ) : (
          <>
            {immediateLabel && periodEndLabel ? (
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#5e3410' }}>
                {t('teamMembers.billing.immediateCharge', {
                  amount: immediateLabel,
                  date: periodEndLabel,
                })}
              </Typography>
            ) : null}
            {periodEndLabel ? (
              <Typography variant="body2" color="text.secondary">
                {t('teamMembers.billing.recurringCharge', {
                  amount: recurringLabel,
                  date: periodEndLabel,
                })}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('teamMembers.billing.recurringChargeNoDate', { amount: recurringLabel })}
              </Typography>
            )}
            <Alert severity="warning">
              {periodEndLabel
                ? t('teamMembers.billing.removalPolicy', { date: periodEndLabel })
                : t('teamMembers.billing.removalPolicyNoDate')}
            </Alert>
          </>
        )}
      </Box>
    </ScrollableDialog>
  );
};

export default TeamMemberBillingConfirmDialog;
